import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'antd';
import {Tool} from '../types'

interface ToolbarProps {
    TOOLS: Tool[];
    selectedTool: string;
    objects: any[];
    selectedObjects: string[];
    draggedTool: string | null;
    onToolSelect: (tool: string) => void;
    onToolDragStart: (e: React.DragEvent, toolKey: string) => void;
    onToolDrag: (e: React.DragEvent) => void;
    onToolDragEnd: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({
    TOOLS,
    selectedTool,
    objects,
    selectedObjects,
    draggedTool,
    onToolSelect,
    onToolDragStart,
    onToolDrag,
    onToolDragEnd,
    onDuplicate,
    onDelete,
}) => {
    const [popOutTool, setPopOutTool] = useState<string | null>(null);
    const [hiddenTool, setHiddenTool] = useState<string | null>(null);
    const [animationKey, setAnimationKey] = useState(0);
    const duplicateButtonRef = useRef<HTMLButtonElement>(null);
    const deleteButtonRef = useRef<HTMLButtonElement>(null);

    // Reset button styles when selection is cleared
    useEffect(() => {
        if (selectedObjects.length === 0) {
            if (duplicateButtonRef.current) {
                duplicateButtonRef.current.style.transform = 'translateY(0px)';
                duplicateButtonRef.current.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }
            if (deleteButtonRef.current) {
                deleteButtonRef.current.style.transform = 'translateY(0px)';
                deleteButtonRef.current.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                deleteButtonRef.current.style.background = '';
            }
        }
    }, [selectedObjects.length]);

    // Hide the tool after a small delay to allow drag to start
    useEffect(() => {
        if (draggedTool) {
            const hideTimer = setTimeout(() => {
                setHiddenTool(draggedTool);
            }, 10); // Small delay to let drag start
            return () => clearTimeout(hideTimer);
        } else if (hiddenTool) {
            // Drag ended, trigger pop-out animation
            // Clear first to ensure re-trigger works for same tool
            setPopOutTool(null);
            // Use requestAnimationFrame to ensure state is cleared before setting new value
            requestAnimationFrame(() => {
                setPopOutTool(hiddenTool);
                setAnimationKey((prev) => prev + 1);
            });
            setHiddenTool(null);
        }
    }, [draggedTool, hiddenTool]);

    // Clear popOutTool after animation completes
    useEffect(() => {
        if (popOutTool) {
            const popTimer = setTimeout(() => {
                setPopOutTool(null);
            }, 300);
            return () => clearTimeout(popTimer);
        }
    }, [popOutTool, animationKey]);

    // Inject keyframes animation
    useEffect(() => {
        const styleId = 'toolbar-popout-animation';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                @keyframes popOut {
                    0% {
                        opacity: 0;
                        transform: scale(0.3);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(1.3);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }, []);
    return (
        <div
            style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '16px',
                background: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                alignItems: 'center',
            }}
        >
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginRight: '8px' }}>
                    TOOLS:
                </span>
                {TOOLS.map((tool) => {
                    const isHidden = hiddenTool === tool.key;
                    const isPopOut = popOutTool === tool.key;

                    return (
                        <Button
                            key={`${tool.key}-${isPopOut ? animationKey : 'static'}`}
                            size="small"
                            type={selectedTool === tool.key ? 'primary' : 'default'}
                            onClick={() => onToolSelect(tool.key)}
                            draggable={tool.key !== 'select'}
                            onDragStart={(e) => onToolDragStart(e, tool.key)}
                            onDrag={onToolDrag}
                            onDragEnd={onToolDragEnd}
                            style={{
                                height: '40px',
                                width: '40px',
                                borderRadius: '50%',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                transition: isHidden || isPopOut ? 'none' : 'all 0.3s ease',
                                transform: 'translateY(0px)',
                                boxShadow: isPopOut
                                    ? '0 8px 16px rgba(24, 144, 255, 0.4)'
                                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                cursor: tool.key === 'select' ? 'pointer' : 'grab',
                                opacity: isHidden ? 0 : 1,
                                visibility: isHidden ? 'hidden' : 'visible',
                                animation: isPopOut ? 'popOut 0.3s ease-out forwards' : 'none',
                            }}
                            onMouseEnter={(e) => {
                                if (!isHidden && !isPopOut) {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isHidden && !isPopOut) {
                                    e.currentTarget.style.transform = 'translateY(0px)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                }
                            }}
                            title={
                                tool.key === 'select'
                                    ? tool.label
                                    : `${tool.label} - Click to select or drag to canvas`
                            }
                        >
                            {tool.icon}
                        </Button>
                    );
                })}
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>
                    {objects.length} objects • {selectedObjects.length} selected
                </span>

                <div style={{ width: '1px', height: '24px', background: '#e8e8e8', margin: '0 8px' }} />

                <Button
                    ref={duplicateButtonRef}
                    size="small"
                    onClick={onDuplicate}
                    disabled={selectedObjects.length === 0}
                    style={{
                        height: '40px',
                        width: '40px',
                        borderRadius: '50%',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        transform: 'translateY(0px)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        cursor: selectedObjects.length === 0 ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                        if (selectedObjects.length > 0) {
                            e.currentTarget.style.transform = 'translateY(-3px)';
                            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0px)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }}
                    title="Duplicate selected objects (Ctrl+D)"
                >
                    📋
                </Button>
                <Button
                    ref={deleteButtonRef}
                    size="small"
                    danger
                    onClick={onDelete}
                    disabled={selectedObjects.length === 0}
                    style={{
                        height: '40px',
                        width: '40px',
                        borderRadius: '50%',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        transform: 'translateY(0px)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        cursor: selectedObjects.length === 0 ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                        if (selectedObjects.length > 0) {
                            e.currentTarget.style.background = '#dd2121';
                            e.currentTarget.style.transform = 'translateY(-3px)';
                            e.currentTarget.style.boxShadow = '0 6px 12px #dd2121';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = '';
                        e.currentTarget.style.transform = 'translateY(0px)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }}
                    title="Delete selected objects (Delete)"
                >
                    🗑️
                </Button>
            </div>
        </div>
    );
};
